import { useUserDirectory } from "@/hooks/use-user-directory";

import {
  ActionConfirmationModal,
  AppModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  SearchableSelectFormField,
} from "@/components/ui";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useCreateMatterUserConnection } from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMedicalTreatmentBalanceConfirmation,
  useDeleteMedicalTreatmentBalanceConfirmation,
  useUpdateMedicalTreatmentBalanceConfirmation,
} from "@/lib/data/react-query/matters/medical-treatment-balance-confirmation";
import { MedicalTreatmentBalanceConfirmationSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { MedicalTreatmentBalanceConfirmation, User } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditTreatmentBalanceConfirmationModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  matterId: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentBalanceConfirmation>;
}

type BalanceConfirmationUserOption = Pick<
  User,
  "id" | "firstName" | "lastName"
> & {
  label?: string | null;
  isTeamMember?: boolean;
};

const balanceConfirmationFormSchema =
  MedicalTreatmentBalanceConfirmationSchema.omit({
    connections: true,
    connectionsNextCursor: true,
    id: true,
    dateCreated: true,
    dateModified: true,
    matterId: true,
    medicalTreatmentId: true,
  }).extend({
    balanceConfirmationUserId: z.string().optional().or(z.literal("")),
  });

const defaultFormValues = {
  confirmed: true,
  amount: null,
  date: "",
  balanceConfirmationUserId: "",
};

const getBalanceConfirmationId = (
  confirmation?: Partial<MedicalTreatmentBalanceConfirmation>,
) => confirmation?.id;

export const AddOrEditTreatmentBalanceConfirmationModal = ({
  title,
  description,
  open,
  setOpen,
  editing,
  matterId,
  treatmentId,
  initialData,
}: AddOrEditTreatmentBalanceConfirmationModalProps) => {
  const { hasActionPermission, currentUser } = useUser();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matterConnections = useMemo(
    () => matterData.data.userConnections,
    [matterData.data.userConnections],
  );
  const getLatestConnectionForConfirmation = useCallback(
    (confirmationId?: string) => {
      if (!confirmationId) return undefined;
      const matches = matterConnections.filter(
        (connection) =>
          connection.medicalTreatmentBalanceConfirmationId === confirmationId,
      );
      if (!matches.length) return undefined;
      return matches.sort((a, b) =>
        dayjs(b.dateCreated).diff(dayjs(a.dateCreated)),
      )[0];
    },
    [matterConnections],
  );

  const balanceConfirmationInitialValues = useMemo(() => {
    const baseValues = initialData ?? {};
    const legacyConnectionId =
      (
        baseValues as MedicalTreatmentBalanceConfirmation & {
          matterUserConnectionId?: string;
          balanceConfirmationUserConnectionId?: string;
        }
      ).matterUserConnectionId ??
      (
        baseValues as MedicalTreatmentBalanceConfirmation & {
          balanceConfirmationUserConnectionId?: string;
        }
      ).balanceConfirmationUserConnectionId;
    const legacyConnection = legacyConnectionId
      ? matterConnections.find(
          (connection) => connection.id === legacyConnectionId,
        )
      : undefined;
    const confirmationId = getBalanceConfirmationId(baseValues);
    const confirmationConnection =
      getLatestConnectionForConfirmation(confirmationId);
    const confirmedByUserId =
      confirmationConnection?.userId ?? legacyConnection?.userId ?? "";
    return {
      ...baseValues,
      id: confirmationId,
      medicalTreatmentBalanceConfirmationId: confirmationId,
      balanceConfirmationUserId: confirmedByUserId,
    };
  }, [getLatestConnectionForConfirmation, initialData, matterConnections]);
  const balanceConfirmationForm = useForm<
    z.infer<typeof balanceConfirmationFormSchema>
  >({
    resolver: zodResolver(balanceConfirmationFormSchema),
    values: {
      ...defaultFormValues,
      ...balanceConfirmationInitialValues,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const userDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [
      balanceConfirmationForm.watch("balanceConfirmationUserId") ?? "",
    ],
  });
  const { users, isLoading: isUsersLoading } = userDirectory;
  const selectedUserId = balanceConfirmationForm.watch(
    "balanceConfirmationUserId",
  );
  const groupedUserOptions = useMemo(() => {
    const getSortLabel = (option: BalanceConfirmationUserOption) =>
      (formatUsersName(option)?.trim() ?? option.id).trim();

    const teamMembers: BalanceConfirmationUserOption[] = [];
    const teamUserIds = new Set<string>();

    const matterAssignees = matterData.data.assignees;

    matterAssignees.forEach((assignee) => {
      if (
        assignee.dateTimeEnd ||
        (assignee.user.enabled === false &&
          assignee.user.id !== selectedUserId) ||
        teamUserIds.has(assignee.user.id)
      ) {
        return;
      }

      teamUserIds.add(assignee.user.id);
      teamMembers.push({
        id: assignee.user.id,
        firstName: assignee.user.firstName,
        lastName: assignee.user.lastName,
        label: assignee.role.label,
        isTeamMember: true,
      });
    });

    teamMembers.sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

    const otherUsers = users
      .filter((user) => user.id && !teamUserIds.has(user.id))
      .map((user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        isTeamMember: false,
      }));

    otherUsers.sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

    const groups: Array<{
      label: string;
      items: BalanceConfirmationUserOption[];
    }> = [];

    if (teamMembers.length > 0) {
      groups.push({
        label: "Assigned on matter",
        items: teamMembers,
      });
    }

    if (otherUsers.length > 0) {
      groups.push({
        label: "Others",
        items: otherUsers,
      });
    }

    return groups;
  }, [matterData.data.assignees, selectedUserId, users]);
  const formatUserLabel = useCallback(
    (user?: BalanceConfirmationUserOption) => {
      if (!user) return "";
      const userName = formatUsersName(user)?.trim();
      if (!userName) return user.id;
      if (user.isTeamMember && user.label) {
        return `${userName} — ${user.label}`;
      }
      return userName;
    },
    [],
  );

  const isFormActuallyDirty =
    Object.keys(balanceConfirmationForm.formState.dirtyFields).length > 0;

  const createBalanceConfirmation =
    useCreateMedicalTreatmentBalanceConfirmation({});
  const deleteBalanceConfirmation =
    useDeleteMedicalTreatmentBalanceConfirmation();
  const updateBalanceConfirmation =
    useUpdateMedicalTreatmentBalanceConfirmation({});
  const createMatterUserConnection = useCreateMatterUserConnection({
    showToast: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const isFormLoading =
    createBalanceConfirmation.isPending ||
    updateBalanceConfirmation.isPending ||
    createMatterUserConnection.isPending;

  const onSubmit = async (
    data: z.infer<typeof balanceConfirmationFormSchema>,
  ) => {
    if (!treatmentId) {
      return;
    }
    const { balanceConfirmationUserId, ...confirmationData } = data;
    const confirmationId = balanceConfirmationInitialValues.id;
    const existingConnection =
      getLatestConnectionForConfirmation(confirmationId);
    if (
      editing &&
      balanceConfirmationInitialValues.id &&
      balanceConfirmationInitialValues.dateModified
    ) {
      await updateBalanceConfirmation.mutateAsync({
        ...confirmationData,
        matterId,
        dateModified: balanceConfirmationInitialValues.dateModified,
        id: balanceConfirmationInitialValues.id,
      });
      if (balanceConfirmationUserId && confirmationId) {
        const shouldCreateConnection =
          !existingConnection ||
          existingConnection.userId !== balanceConfirmationUserId;
        if (shouldCreateConnection) {
          const selectedUser = users.find(
            (user) => user.id === balanceConfirmationUserId,
          );
          if (selectedUser) {
            await createMatterUserConnection.mutateAsync({
              matterId,
              label: "Balance confirmed by",
              medicalTreatmentBalanceConfirmationId: confirmationId,
              userId: selectedUser.id,
            });
          }
        }
      }
    } else {
      const createdConfirmation = await createBalanceConfirmation.mutateAsync({
        matterId,
        medicalTreatmentId: treatmentId,
        ...confirmationData,
      });
      if (balanceConfirmationUserId) {
        const selectedUser = users.find(
          (user) => user.id === balanceConfirmationUserId,
        );
        if (selectedUser) {
          await createMatterUserConnection.mutateAsync({
            matterId,
            label: "Balance confirmed by",
            medicalTreatmentBalanceConfirmationId: createdConfirmation.data.id,
            userId: selectedUser.id,
          });
        }
      }
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      balanceConfirmationForm.reset({
        ...defaultFormValues,
        ...balanceConfirmationInitialValues,
      });
    }
  }, [open, balanceConfirmationForm, balanceConfirmationInitialValues]);
  useEffect(() => {
    if (open && !editing) {
      const currentValue = balanceConfirmationForm.getValues(
        "balanceConfirmationUserId",
      );
      if (!currentValue && currentUser.id) {
        balanceConfirmationForm.setValue(
          "balanceConfirmationUserId",
          currentUser.id,
          {
            shouldDirty: false,
            shouldValidate: false,
          },
        );
      }
    }
  }, [open, editing, balanceConfirmationForm, currentUser.id]);

  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBalanceConfirmation,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBalanceConfirmation,
    ACTION_PERMISSION_VALUES.delete,
  );

  return (
    <>
      <Form {...balanceConfirmationForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="md"
          overlayClassName="z-[104]"
          className="z-[105]"
          loading={isFormLoading}
          isDirty={isFormActuallyDirty}
          bodyFormId="balance-confirmation-form"
          bodyFormOnSubmit={balanceConfirmationForm.handleSubmit(onSubmit)}
          footer={
            <div className="flex items-center gap-2">
              {editing && hasDeletePermission && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setIsDeleting(true);
                  }}
                  data-testid="delete-balance-confirmation-button"
                >
                  Delete
                </Button>
              )}
              <div className="flex grow justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  submitFormId="balance-confirmation-form"
                  disabled={isFormLoading}
                  title="Save"
                  overrideDisabled={!editing}
                />
              </div>
            </div>
          }
        >
          <fieldset
            disabled={!hasEditPermission || isFormLoading}
            className="grid gap-4"
          >
            <SearchableSelectFormField<BalanceConfirmationUserOption>
              label="Confirmed by"
              placeholder="Select a user"
              groupedItems={groupedUserOptions}
              {...userDirectory.selectProps}
              isItemsLoading={isUsersLoading}
              itemsKey="id"
              formatItemLabel={formatUserLabel}
              formatSelectedItemLabel={formatUserLabel}
              register={balanceConfirmationForm.register(
                "balanceConfirmationUserId",
              )}
            />
            <CurrencyFormField
              label="Confirmed amount"
              register={balanceConfirmationForm.register("amount")}
            />
            <DateFormField
              label="Confirmation date"
              register={balanceConfirmationForm.register("date")}
              name="date"
            />
          </fieldset>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete balance confirmation"
        description="Are you sure you want to delete this balance confirmation? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={() => {
          const confirmationId = balanceConfirmationInitialValues.id;
          if (!confirmationId) return;
          deleteBalanceConfirmation.mutate({
            matterId,
            id: confirmationId,
          });
          setIsDeleting(false);
          setOpen(false);
        }}
      />
    </>
  );
};
